import React, { useEffect, useState, useCallback } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import useMasterclass from './hooks/useMasterclass';
import Header from './components/Header';
import Hero from './components/Hero';
import VideoSection from './components/VideoSection';
import HowItWorks from './components/HowItWorks';
import PricingCards from './components/PricingCards';
import Problem from './components/Problem';
import Solution from './components/Solution';
import Challenge from './components/Challenge';
import FlowDiagram from './components/FlowDiagram';
import SuccessStory from './components/SuccessStory';
import Testimonials from './components/Testimonials';
import Founder from './components/Founder';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import WebinarModal from './components/WebinarModal';
import MasterclassBanner from './components/MasterclassBanner';
import MasterclassApplicationModal from './components/MasterclassApplicationModal';
import Toast from './components/Toast';
import BookCallButton from './components/BookCallButton';
import SchemaMarkup from './components/SchemaMarkup';
import TrackingScripts from './components/TrackingScripts';
import './App.css';

// Stripe Payment URL - Uses environment variable with fallback
const STRIPE_PAYMENT_URL = process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://myeaitradingcompass.com/trading-subscription';

function App() {
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);
  const [showBanner, setShowBanner] = useState(() => {
    // Check if banner was dismissed in this session
    return sessionStorage.getItem('masterclass_banner_dismissed') !== 'true';
  });
  
  // Masterclass system integration (replaces webinar)
  const { 
    masterclassData, 
    loading,
    showModal: showMasterclassModal, 
    toast,
    openModal: openMasterclassModal, 
    closeModal: closeMasterclassModal, 
    handleApply,
    clearToast 
  } = useMasterclass();

  // Handle referral tracking on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
      console.log('Referral code stored:', refCode);
    }
  }, []);

  // Exit Intent Detection - Show masterclass modal when user tries to leave
  const handleExitIntent = useCallback((e) => {
    // Only trigger if mouse leaves from the top of the viewport
    if (e.clientY <= 0 && !exitIntentShown && !showMasterclassModal && masterclassData) {
      // Check if exit intent was shown recently (within 1 hour)
      const lastShown = localStorage.getItem('exit_intent_shown');
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      
      if (!lastShown || parseInt(lastShown) < oneHourAgo) {
        setExitIntentShown(true);
        openMasterclassModal();
        localStorage.setItem('exit_intent_shown', Date.now().toString());
      }
    }
  }, [exitIntentShown, showMasterclassModal, masterclassData, openMasterclassModal]);

  useEffect(() => {
    // Add exit intent listener
    document.addEventListener('mouseleave', handleExitIntent);
    
    return () => {
      document.removeEventListener('mouseleave', handleExitIntent);
    };
  }, [handleExitIntent]);

  // Smart modal switching: Masterclass takes priority when active
  const handleCTAClick = () => {
    if (!loading && masterclassData) {
      // Masterclass is active - open masterclass modal
      openMasterclassModal();
    } else {
      // No masterclass - open challenge modal (fallback)
      setChallengeModalOpen(true);
    }
  };

  // Check if banner should be shown (not dismissed in session and masterclass is active)
  const bannerVisible = !loading && masterclassData && showBanner;
  // Padding for both banner + header
  // Mobile: banner (~140px) + header (56px) = 196px
  // Desktop: banner (~56px) + header (64px) = 120px
  const contentPadding = bannerVisible ? 'pt-[196px] md:pt-[120px]' : 'pt-[56px] md:pt-[64px]';

  return (
    <HelmetProvider>
      <div className="App">
        <SchemaMarkup />
        <TrackingScripts />
        
        {/* Masterclass Banner - Fixed at top, only show if masterclass is active and not dismissed */}
        {bannerVisible && (
          <MasterclassBanner 
            onApplyClick={openMasterclassModal} 
            masterclassData={masterclassData}
            onDismiss={() => setShowBanner(false)}
          />
        )}
        
        {/* Main content with padding for fixed banner + header */}
        <div className={contentPadding}>
          <Header hasBanner={bannerVisible} />
          <main>
            <Hero onOpenModal={handleCTAClick} stripeUrl={STRIPE_PAYMENT_URL} />
            <VideoSection />
            <HowItWorks />
            {/* Pricing Section - After video, before other content */}
            <section id="pricing-section" className="py-10 md:py-16 bg-slate-50">
              <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
                <PricingCards stripeUrl={STRIPE_PAYMENT_URL} />
              </div>
            </section>
            <Problem />
            <Solution />
            <Challenge onOpenModal={handleCTAClick} />
            <FlowDiagram />
            <SuccessStory />
            <Testimonials />
            <Founder />
            <FAQ />
            <FinalCTA onOpenModal={handleCTAClick} />
          </main>
          <Footer />
        </div>
        
        {/* Masterclass Application Modal (Priority) */}
        <MasterclassApplicationModal
          isOpen={showMasterclassModal}
          onClose={closeMasterclassModal}
          onSubmit={handleApply}
          masterclassData={masterclassData}
        />
        
        {/* Challenge Modal (Fallback when no masterclass) */}
        <WebinarModal open={challengeModalOpen} onOpenChange={setChallengeModalOpen} />
        
        {/* Toast Notifications */}
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={clearToast} 
          />
        )}
        
        {/* Floating Book a Call Button */}
        <BookCallButton />
        
        <Toaster position="top-center" />
      </div>
    </HelmetProvider>
  );
}

export default App;
